// Generic date helpers — no domain coupling. Two kinds of value live here and
// must not be mixed: an instant (a moment in time) and a date-only value.
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

// The stored shapes of a date-only value. Strict-parsed, so anything else is
// left alone rather than guessed at.
const DATE_ONLY_FORMATS = ['YYYY/MM/DD', 'YYYY/M/D'];
const DATE_ONLY_DISPLAY = 'YYYY/MM/DD';

// Locale-formatted calendar date for an INSTANT (an ISO timestamp or Date).
// A date-only value has no instant — use formatDateOnly, or it shifts a day.
export const formatDate = (value: string | Date): string => new Date(value).toLocaleDateString();

// A date-only value (a birthday, an anniversary) has no time and no timezone, so
// it never becomes a Date here. See "Date-only values never become instants" in CLAUDE.md.
export const formatDateOnly = (value?: string | null): string => {
  const raw = (value || '').trim();
  if (!raw) return '';

  const parsed = dayjs(raw, DATE_ONLY_FORMATS, true);
  return parsed.isValid() ? parsed.format(DATE_ONLY_DISPLAY) : raw;
};

export const isValidDateOnly = (value?: string | null): boolean =>
  !!value && dayjs(value.trim(), DATE_ONLY_FORMATS, true).isValid();

// After the viewer's own today. The server allows up to today at UTC+14, so it never refuses what this accepts.
export const isFutureDateOnly = (value?: string | null): boolean =>
  isValidDateOnly(value) && dayjs((value as string).trim(), DATE_ONLY_FORMATS, true).isAfter(dayjs(), 'day');

// Epoch milliseconds, for chronological sorting/comparison. Accepts a number because some server
// paths send epoch ms; typing it out forced casts at call sites and hid that from a refactor.
export const toEpochMillis = (value: string | number | Date): number => new Date(value).getTime();

// A countdown as m:ss (e.g. 1:05); negative durations read as 0:00.
export const formatCountdown = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
};
